// src/components/ComprobantePDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#ffffff',
  },
  header: {
    backgroundColor: '#1a202c',
    padding: 20,
    marginBottom: 20,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#a0aec0',
  },
  section: {
    marginBottom: 14,
    padding: 14,
    backgroundColor: '#f7fafc',
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#2d3748',
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  label: {
    fontSize: 10,
    color: '#718096',
  },
  value: {
    fontSize: 10,
    color: '#2d3748',
    fontWeight: 'normal',
  },
  totalContainer: {
    backgroundColor: '#e83e8c',
    padding: 14,
    borderRadius: 4,
    marginTop: 6,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  totalValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  totalValueMoneda: {
    fontSize: 22,
    color: '#ffffff',
    fontWeight: 'bold',
    marginRight: 6,
  },
  totalValueNumero: {
    fontSize: 22,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  referenciaContainer: {
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.25)',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  referenciaLabel: {
    fontSize: 9,
    color: 'rgba(255,255,255,0.75)',
  },
  referenciaValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  referenciaMoneda: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.85)',
    fontWeight: 'bold',
    marginRight: 4,
  },
  referenciaNumero: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.85)',
    fontWeight: 'bold',
  },
  footer: {
    marginTop: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: '#a0aec0',
  },
});

// ✅ Formateador manual: miles con punto, decimales con coma
const formatearMonto = (monto, decimales = 2) => {
  if (monto == null || isNaN(monto)) return '0';
  const numero = Number(monto).toFixed(decimales);
  const [entero, decimal] = numero.split('.');
  const enteroConPuntos = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimales > 0 ? `${enteroConPuntos},${decimal}` : enteroConPuntos;
};

const ComprobantePDF = ({
  reserva,
  servicio,
  titular,
  pasajerosAdicionales,
  metodoPago,
  comprobante,
  cantidadPasajeros,
  monedaMetodoPago = null,
  montoConvertido = null,
}) => {
  if (!servicio || !titular || !reserva) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={{ fontSize: 20, textAlign: 'center', marginTop: 50 }}>
            Error: Datos incompletos para generar el comprobante
          </Text>
        </Page>
      </Document>
    );
  }

  const total = servicio.precio * cantidadPasajeros;
  const fechaActual = new Date().toLocaleString();

  const mostrarConvertido =
    montoConvertido != null &&
    (monedaMetodoPago === 'VES' || monedaMetodoPago === 'BRL');

  // ✅ Prefijo alfabético (sin símbolos raros)
  const prefijoMoneda =
    monedaMetodoPago === 'VES' ? 'Bs' :
    monedaMetodoPago === 'BRL' ? 'R$' : 'USD';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>Comprobante de Reserva</Text>
            <Text style={styles.headerSubtitle}>
              N° de reserva: {reserva.codigo || 'Sin código'}
            </Text>
          </View>
        </View>

        {/* Datos de la reserva */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Datos de la reserva</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Fecha de compra:</Text>
            <Text style={styles.value}>{fechaActual}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Método de pago:</Text>
            <Text style={styles.value}>{metodoPago || 'No especificado'}</Text>
          </View>
          {comprobante && (
            <View style={styles.row}>
              <Text style={styles.label}>Comprobante:</Text>
              <Text style={styles.value}>{comprobante}</Text>
            </View>
          )}
        </View>

        {/* Datos del viaje */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Datos del viaje</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Origen:</Text>
            <Text style={styles.value}>{servicio.horario?.origen || 'No especificado'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Destino:</Text>
            <Text style={styles.value}>{servicio.horario?.destino || 'No especificado'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Fecha:</Text>
            <Text style={styles.value}>
              {servicio.fecha ? new Date(servicio.fecha).toLocaleDateString() : 'No especificada'}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Hora de salida:</Text>
            <Text style={styles.value}>{servicio.horario?.salida || 'No especificada'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Equipaje:</Text>
            <Text style={styles.value}>1 Maleta de 23kg por pasaje</Text>
          </View>
        </View>

        {/* Datos del titular */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Datos del titular</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Nombre:</Text>
            <Text style={styles.value}>{titular.nombre || 'No especificado'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Cédula:</Text>
            <Text style={styles.value}>{titular.cedula || 'No especificada'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>WhatsApp:</Text>
            <Text style={styles.value}>{titular.telefonoWhatsapp || 'No especificado'}</Text>
          </View>
          {titular.telefonoParticular && (
            <View style={styles.row}>
              <Text style={styles.label}>Teléfono particular:</Text>
              <Text style={styles.value}>{titular.telefonoParticular}</Text>
            </View>
          )}
        </View>

        {/* Pasajeros adicionales */}
        {pasajerosAdicionales && pasajerosAdicionales.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Pasajeros adicionales ({pasajerosAdicionales.length})
            </Text>
            {pasajerosAdicionales.map((pasajero, index) => (
              <View key={index}>
                <View style={styles.row}>
                  <Text style={styles.label}>Pasajero {index + 1}:</Text>
                  <Text style={styles.value}>{pasajero.nombre || 'Sin nombre'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Cédula:</Text>
                  <Text style={styles.value}>{pasajero.cedula || 'Sin cédula'}</Text>
                </View>
                {index < pasajerosAdicionales.length - 1 && (
                  <View style={{ height: 4 }} />
                )}
              </View>
            ))}
          </View>
        )}

        {/* Total con conversión */}
        <View style={styles.totalContainer}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              {mostrarConvertido ? 'TOTAL A PAGAR' : 'TOTAL'}
            </Text>
            <View style={styles.totalValueRow}>
              <Text style={styles.totalValueMoneda}>
                {mostrarConvertido ? prefijoMoneda : 'USD'}
              </Text>
              <Text style={styles.totalValueNumero}>
                {mostrarConvertido
                  ? formatearMonto(montoConvertido, 2)
                  : formatearMonto(total, 0)}
              </Text>
            </View>
          </View>

          {mostrarConvertido && (
            <View style={styles.referenciaContainer}>
              <Text style={styles.referenciaLabel}>Referencia</Text>
              <View style={styles.referenciaValueRow}>
                <Text style={styles.referenciaMoneda}>USD</Text>
                <Text style={styles.referenciaNumero}>
                  {formatearMonto(total, 0)}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generado el {fechaActual}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ComprobantePDF;